import type { VFC } from 'react';
import React, { useContext, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { PageTitle } from '@confluence/page-title';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { getStreamInject } from '@confluence/ssr-data-streaming/entry-points/server.ts';
import { stringifyForSSR } from '@confluence/ssr-utilities/entry-points/stringifyForSSR.ts';

const i18n = defineMessages({
	notFound: {
		id: 'document-title.not.found',
		defaultMessage: 'Page Not Found',
		description: 'title of the 404 page not found page',
	},
});

type DocumentContentTitleProps = {
	contentTitle?: string;
	contentSpace?: string;
	siteTitleOnly?: boolean;
	titlePrefix?: string;
	notFound?: boolean;
	isHomePage?: boolean;
};

export const DocumentContentTitle: VFC<DocumentContentTitleProps> = ({
	titlePrefix = '',
	contentTitle = '',
	contentSpace = '',
	siteTitleOnly = false,
	notFound = false,
	isHomePage = false,
}) => {
	const intl = useIntl();
	const { siteTitle } = useContext(SPAViewContext);
	useEffect(() => {
		return () => {
			window['__DOCUMENT_TITLE_TAG__'] = '';
		};
	}, []);

	// In the case when page is rendered by SSR, the __DOCUMENT_TITLE_TAG__ will be
	// populated and we can skip document title.
	if (window['__DOCUMENT_TITLE_TAG__'] && window['__SSR_RENDERED__'] && document.title) {
		return null;
	}

	const title = [
		titlePrefix || (notFound ? intl.formatMessage(i18n.notFound) : ''),
		siteTitleOnly ? undefined : contentTitle,
		siteTitleOnly ? undefined : contentSpace,
		siteTitle,
	]
		.filter((part) => part)
		.join(' - ');

	// Don't override an existing title with a less specific one (e.g., don't set "Confluence"
	// when there's already a title with content like "Page Title - Space - Confluence")
	// This prevents components with empty contentTitle from overriding titles set by components
	// with actual content during navigation/rendering transitions.
	// Note: This check happens during render, so it may not catch titles set in the same render cycle,
	// but useLayoutEffect in PageTitle ensures the last component to render wins.
	if (typeof document !== 'undefined' && document.title && !contentTitle) {
		// If contentTitle is empty, we're likely setting a generic title (just siteTitle or siteTitle with space)
		// If there's already a more specific title (3+ parts, or 2 parts where first isn't siteTitle), skip
		const existingTitleParts = document.title.split(' - ');
		const hasMoreSpecificContent =
			existingTitleParts.length > 2 ||
			(existingTitleParts.length === 2 && existingTitleParts[0] !== siteTitle);

		// Skip if we're trying to set a less specific title when a more specific one exists and it is not the home page
		if (hasMoreSpecificContent && title !== document.title && !isHomePage) {
			return null;
		}
	}

	// Set for SSR document title
	window['__DOCUMENT_TITLE_TAG__'] = title;

	if (window.__SSR_REACT_STREAMING__) {
		const inject = getStreamInject();

		inject(() => {
			return React.createElement('script', {
				dangerouslySetInnerHTML: {
					__html: `document.title=${stringifyForSSR(title)};`,
				},
			});
		});
	}

	return <PageTitle title={title} />;
};
